import { Button } from '@heidi/design-system/button';
import { cn } from '@heidi/utils';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
type Props = {
  total: number;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  isLoading?: boolean;
};

export const Pagination = ({
  total,
  page,
  setPage,
  pageSize,
  isLoading = false,
}: Props) => {
  const numberOfPages = Math.ceil(total / pageSize);

  const getPaginationItems = useCallback(
    (totalPages: number, currentPage: number): (number | string)[] => {
      let pages: (number | string)[] = [];

      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 4) {
          pages = [1, 2, 3, 4, 5, '...', totalPages];
        } else if (currentPage >= totalPages - 3) {
          pages = [
            1,
            '...',
            totalPages - 4,
            totalPages - 3,
            totalPages - 2,
            totalPages - 1,
            totalPages,
          ];
        } else {
          pages = [
            1,
            '...',
            currentPage - 1,
            currentPage,
            currentPage + 1,
            '...',
            totalPages,
          ];
        }
      }
      return pages;
    },
    []
  );

  const pages = getPaginationItems(numberOfPages, page);

  return (
    <div className="flex w-fit items-center justify-center rounded-lg px-4 py-2 backdrop-blur-lg">
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          disabled={page === 1 || isLoading}
          onClick={() => setPage(page - 1)}
        >
          <ChevronLeft className="rtl:-scale-x-100" />
          <span className="sr-only md:not-sr-only">
            <FormattedMessage
              defaultMessage="Previous"
              id="+8Sb9PKYE8"
              description="Pagination button label for the previous page"
            />
          </span>
        </Button>
        {pages.map((pageNumber, index) => {
          if (typeof pageNumber === 'string') {
            return (
              <div key={index} className="p-2">
                <MoreHorizontal className="size-4" />
              </div>
            );
          }
          const isActive = page === pageNumber;
          return (
            <Button
              variant={isActive ? 'surface' : 'ghost'}
              size="sm"
              key={index}
              disabled={isLoading}
              tabIndex={isActive ? -1 : undefined}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                // Button sizing doesn't guarantee a square when the label is
                // narrower than the height, so floor the width to keep a
                // single-digit page at least square (it still grows with more
                // digits).
                'min-w-8 px-2 transition-colors duration-200 ease-out',
                isActive && 'pointer-events-none border-border-selected'
              )}
              onClick={() => {
                if (isActive) return;
                setPage(pageNumber);
              }}
            >
              {pageNumber}
            </Button>
          );
        })}
        <Button
          variant="ghost"
          size="sm"
          disabled={page === numberOfPages || isLoading}
          onClick={() => setPage(page + 1)}
        >
          <span className="sr-only md:not-sr-only">
            <FormattedMessage
              defaultMessage="Next"
              id="VvvJ6atvFg"
              description="Pagination button label for the next page"
            />
          </span>
          <ChevronRight className="rtl:-scale-x-100" />
        </Button>
      </div>
    </div>
  );
};
